import { api } from './api';

// Types
export interface TelegramLinkInstructions {
  code: string;
  botLink: string;
  instructions: string[];
  expiresIn: string;
}

export interface TelegramStatus {
  isLinked: boolean;
  username: string | null;
  linkedAt: string | null;
  notificationsEnabled: boolean;
}

export interface WhatsAppGroupInfo {
  groupLink: string | null;
  groupName: string;
  isConfigured: boolean;
}

export interface MessagingConfigStatus {
  telegram: {
    configured: boolean;
    botUsername: string | null;
    adminChatConfigured: boolean;
  };
  whatsapp: {
    apiConfigured: boolean;
    groupConfigured: boolean;
    groupLink: string | null;
  };
}

export interface BroadcastResult {
  telegram: {
    adminGroup: string;
    users: {
      sent: number;
      failed: number;
      skipped: number;
    };
  };
  whatsapp: {
    sent: number;
    failed: number;
    skipped: number;
  } | null;
}

// Messaging API
export const messagingAPI = {
  // ==================== TELEGRAM ====================
  
  /**
   * Generate a link code for Telegram
   */
  generateTelegramLink: async (): Promise<{ success: boolean; data: TelegramLinkInstructions }> => {
    const response = await api.post('/messaging/telegram/generate-link');
    return response.data;
  },

  /**
   * Get Telegram link status
   */
  getTelegramStatus: async (): Promise<{ success: boolean; data: TelegramStatus }> => {
    const response = await api.get('/messaging/telegram/status');
    return response.data;
  },

  /**
   * Unlink Telegram account
   */
  unlinkTelegram: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/messaging/telegram/unlink');
    return response.data;
  },

  /**
   * Toggle Telegram notifications
   */
  toggleTelegramNotifications: async (enabled: boolean): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/messaging/telegram/notifications', { enabled });
    return response.data;
  },

  // ==================== WHATSAPP ====================

  /**
   * Get WhatsApp group info
   */
  getWhatsAppGroup: async (): Promise<{ success: boolean; data: WhatsAppGroupInfo }> => {
    const response = await api.get('/messaging/whatsapp/group');
    return response.data;
  },

  /**
   * Toggle WhatsApp notifications
   */
  toggleWhatsAppNotifications: async (enabled: boolean): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/messaging/whatsapp/notifications', { enabled });
    return response.data;
  },

  // ==================== ADMIN ====================

  /**
   * Get messaging configuration status (Admin only)
   */
  getConfigStatus: async (): Promise<{ success: boolean; data: MessagingConfigStatus }> => {
    const response = await api.get('/messaging/config-status');
    return response.data;
  },

  /**
   * Broadcast message to users (Admin only)
   */
  broadcast: async (data: {
    message: string;
    title?: string;
    targetUsers?: 'all' | 'users' | 'agents' | 'admins';
    channels?: ('telegram' | 'whatsapp')[];
  }): Promise<{ success: boolean; message: string; results: BroadcastResult }> => {
    const response = await api.post('/messaging/broadcast', data);
    return response.data;
  },

  /**
   * Send test notification (Admin only)
   */
  sendTest: async (channel: 'telegram' | 'whatsapp', message?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/messaging/test', { channel, message });
    return response.data;
  },
};

export default messagingAPI;
